import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

import Paginator from '../../components/ui/Paginator';
import Message from '../../components/ui/Message';
import ReportDateRange from '../../components/ui/ReportDateRange';
import { Link } from '../../models/interfaces';
import { LinkPagoContext } from '../../context/linkPago/LinkPagoContext';


const PAGE_SIZE = 10;

type Group = 'todos' | 'hoy';

// Color del estado del link según su texto
const statusBadge = (status?: string | null) => {
    const value = String(status ?? '').toLowerCase();
    if (/pagad/.test(value)) return 'bg-success-subtle text-success-emphasis';
    if (/pend|activ|cread/.test(value)) return 'bg-warning-subtle text-warning-emphasis';
    if (/anulad|rechaz|error/.test(value)) return 'bg-danger-subtle text-danger-emphasis';
    return 'bg-secondary-subtle text-secondary-emphasis';
};

const formatMoney = (amount: number) =>
    Number(amount ?? 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        useGrouping: true,
        style: 'decimal',
    });

const normalizeAmonto = (valor) => {
    // Elimina espacios y reemplaza comas por puntos
    const num = parseFloat(String(valor).trim().replace(',', '.'));
    return isNaN(num) ? "0.00" : num.toFixed(2);
}

export const LinksPagoAdminPage = () => {
    const [buscar, setBuscar] = useState('');
    const [pageActual, setPageActual] = useState<number>(1);
    const { linkPagos, loadLinksPago, loadLinkPagoHoy, isLoading, voidLinkPago, cancelLinkPago, voidAutorizationLink } = useContext(LinkPagoContext);
    const navigate = useNavigate();

    const [actualGroup, setActualGroup] = useState<Group>('todos');
    const [dateIni, setDateIni] = useState(moment().format("YYYY-MM-DD"));
    const [dateEnd, setDateEnd] = useState(moment().add(1, 'day').format("YYYY-MM-DD"));
    // Rango con el que se hizo la última consulta (se usa para recargar después de cancelar/anular)
    const [appliedRange, setAppliedRange] = useState({ FechaInicio: dateIni, FechaFin: dateEnd });
    const [currentLink, setCurrentLink] = useState<Link | null>(null);
    const [copiedId, setCopiedId] = useState<number | null>(null);

    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [voidMessage, setVoidMessage] = useState('');

    useEffect(() => {
        loadLinksPago(appliedRange);
    }, []);

    useEffect(() => {
        setPageActual(1);
    }, [buscar, actualGroup]);

    const filteredData = useMemo(() => {
        const term = buscar.trim().toLowerCase();
        if (!term) return linkPagos;
        return linkPagos.filter((linkPago) =>
            [linkPago.referencia, linkPago.urlUnica, linkPago.nombreCliente]
                .some(val => String(val ?? '').toLowerCase().includes(term))
        );
    }, [linkPagos, buscar]);

    const totalRecords = filteredData.length;
    const totalPage = Math.max(1, Math.ceil(totalRecords / PAGE_SIZE));
    const pageItems = filteredData.slice((pageActual - 1) * PAGE_SIZE, pageActual * PAGE_SIZE);
    const startIndex = totalRecords === 0 ? 0 : (pageActual - 1) * PAGE_SIZE + 1;
    const endIndex = Math.min(pageActual * PAGE_SIZE, totalRecords);

    useEffect(() => {
        if (pageActual > totalPage) setPageActual(totalPage);
    }, [totalPage]);

    const reload = (group: Group = actualGroup, range = appliedRange) => {
        if (group == 'todos') {
            loadLinksPago(range);
        } else {
            loadLinkPagoHoy();
        }
    }

    const changeGroup = (group: Group) => {
        setActualGroup(group);
        reload(group);
    }

    const handleCallback = (start, end) => {
        setDateIni(start.format('YYYY-MM-DD'));
        setDateEnd(moment(end).add(1, 'day').format("YYYY-MM-DD"))
    }

    const handleFilter = () => {
        const range = { FechaInicio: dateIni, FechaFin: dateEnd };
        setAppliedRange(range);
        setPageActual(1);
        reload('todos', range);
    }

    const handleNew = () => {
        navigate("/create-link-pago");
    }

    const handleCopy = async (link: Link) => {
        try {
            await navigator.clipboard.writeText(link.urlUnica);
            setCopiedId(link.id);
            setTimeout(() => setCopiedId(null), 1500);
        } catch (e) {
            setErrorMessage('No se pudo copiar el link, cópielo manualmente: ' + link.urlUnica);
        }
    }

    const handleVoid = (link: Link) => {
        setCurrentLink(link);
        setVoidMessage(actualGroup == 'todos'
            ? `¿Desea cancelar el link de pago de ${link.nombreCliente}?`
            : `¿Desea anular el pago del link de ${link.nombreCliente}?`);
    }

    const processVoid = async (reason?: string) => {
        setVoidMessage("");
        if (!currentLink) return;

        let resp = "";
        if (actualGroup == 'todos') {
            resp = await cancelLinkPago({
                "link": currentLink.id,
                "razón": reason ?? ""
            });
        } else {
            if (!currentLink.puedeAnular) {
                setErrorMessage("El link de pago no se encuentra en un estado válido para anular.");
                setCurrentLink(null);
                return;
            }

            const respVoidAuth = await voidAutorizationLink({
                amount: normalizeAmonto(currentLink.monto),
                currency: currentLink.currency,
                rrn: currentLink.rrn,
                intRef: currentLink.intRef,
                orderId: currentLink.orderId,
                authCode: currentLink.authCode
            });

            if (respVoidAuth != null && respVoidAuth.response.card == "" && respVoidAuth.response.amount == "0.00") {
                resp = await voidLinkPago(currentLink.pagoId);
            } else {
                resp = "No es posible anular la transacción.";
            }
        }

        if (resp === 'success') {
            setSuccessMessage(actualGroup == 'todos' ? 'El link de pago fue cancelado.' : 'El pago fue anulado.');
        } else if (resp) {
            setErrorMessage(resp);
        }
        setCurrentLink(null);
        reload();
    }

    const renderBody = () => {
        if (isLoading) {
            return (
                <tr><td colSpan={7} className='text-center py-4'>
                    <span className="spinner-border spinner-border-sm me-2" /> Cargando...
                </td></tr>
            );
        }
        if (pageItems.length === 0) {
            return (
                <tr><td colSpan={7} className='text-center py-4 text-muted'>
                    {buscar
                        ? 'No se encontraron links para la búsqueda.'
                        : actualGroup == 'todos' ? 'No existen links para el rango seleccionado.' : 'No hay links pagados hoy.'}
                </td></tr>
            );
        }
        return pageItems.map(linkPago => {
            const estado = actualGroup == "todos" ? linkPago.estado : "PAGADO";
            return (
                <tr key={linkPago.id}>
                    <td className="fw-medium" style={styles.ellipsis}>{linkPago.nombreCliente}</td>
                    <td className="text-muted" style={styles.nowrap}>{linkPago.referencia || '-'}</td>
                    <td><span className={`badge rounded-pill ${statusBadge(estado)}`}>{estado}</span></td>
                    <td className="text-end fw-semibold" style={styles.nowrap}>{formatMoney(linkPago.monto)} {linkPago.currency}</td>
                    <td style={styles.nowrap}>{moment(linkPago.fechaCreacion).format('DD/MM/YYYY HH:mm')}</td>
                    <td style={styles.nowrap}>
                        {actualGroup == "todos"
                            ? moment(linkPago.fechaVencimiento).format('DD/MM/YYYY')
                            : moment(linkPago.fechaPago).format('DD/MM/YYYY HH:mm')}
                    </td>
                    <td style={{ textAlign: 'center', width: 110, whiteSpace: 'nowrap' }}>
                        {linkPago.urlUnica && (
                            <button
                                type="button"
                                className={`btn btn-sm ${copiedId === linkPago.id ? 'btn-success' : 'btn-outline-secondary'}`}
                                title="Copiar link"
                                onClick={() => handleCopy(linkPago)}
                            >
                                <i className={`bi ${copiedId === linkPago.id ? 'bi-check-lg' : 'bi-link-45deg'}`} />
                            </button>
                        )}
                        <button
                            type="button"
                            className="btn btn-sm btn-danger ms-1"
                            title={actualGroup == 'todos' ? 'Cancelar link' : 'Anular pago'}
                            onClick={() => handleVoid(linkPago)}
                        >
                            <i className={`bi ${actualGroup == 'todos' ? 'bi-x-circle' : 'bi-arrow-counterclockwise'}`} />
                        </button>
                    </td>
                </tr>
            );
        });
    };

    return (
        <div className='mt-5 container-fluid px-4' style={styles.container}>

            <div className="d-flex flex-wrap justify-content-between align-items-start mb-4">
                <div>
                    <h3 className="mb-1">Links de pago</h3>
                    <p className="text-muted mb-0">Consulte, cancele o anule los links de pago enviados a sus clientes.</p>
                </div>
                <div className="d-flex gap-2 mt-2 mt-md-0">
                    <button type="button" className="btn btn-primary" onClick={handleNew}>
                        <i className="bi bi-plus-lg me-1" /> Crear link de pago
                    </button>
                </div>
            </div>

            <div className="card border-0 shadow-sm">
                <div className="card-body">

                    <ul className="nav nav-tabs mb-3">
                        <li className="nav-item">
                            <button className={`nav-link ${actualGroup == 'todos' ? 'active' : ''}`} onClick={() => changeGroup('todos')}>
                                <i className="bi bi-list-ul me-1" /> Todos
                            </button>
                        </li>
                        <li className="nav-item">
                            <button className={`nav-link ${actualGroup == 'hoy' ? 'active' : ''}`} onClick={() => changeGroup('hoy')}>
                                <i className="bi bi-check2-circle me-1" /> Pagados hoy
                            </button>
                        </li>
                    </ul>

                    <div className="row g-2 mb-3 align-items-end">
                        {actualGroup == 'todos' && (
                            <>
                                <div className="col-md-5 col-lg-3">
                                    <label className="form-label small text-muted mb-1">Rango de fechas (máx. 1 mes)</label>
                                    <ReportDateRange onChange={handleCallback} minDate={moment().subtract(1, 'year')} maxSpan={{ months: 1 }} />
                                </div>
                                <div className="col-md-3 col-lg-2 d-grid">
                                    <button className="btn btn-primary" onClick={handleFilter} disabled={isLoading}>
                                        <i className="bi bi-funnel me-1" /> Filtrar
                                    </button>
                                </div>
                            </>
                        )}
                        <div className={actualGroup == 'todos' ? 'col-md-4 col-lg-4 ms-lg-auto' : 'col-md-6'}>
                            <div className="input-group">
                                <span className="input-group-text bg-white"><i className="bi bi-search" /></span>
                                <input
                                    className="form-control"
                                    id="search"
                                    type="text"
                                    placeholder="Buscar por cliente, referencia o link..."
                                    value={buscar}
                                    onChange={(e) => setBuscar(e.target.value)}
                                />
                                {buscar && (
                                    <button className="btn btn-outline-secondary" type="button" title="Limpiar búsqueda" onClick={() => setBuscar('')}>
                                        <i className="bi bi-x-lg" />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className='table-responsive'>
                        <table id='linksPagoTable' className="table table-hover align-middle">
                            <thead>
                                <tr className="text-muted small text-uppercase">
                                    <th>Cliente</th>
                                    <th>Referencia</th>
                                    <th>Estado</th>
                                    <th className="text-end">Monto</th>
                                    <th>Creación</th>
                                    <th>{actualGroup == 'todos' ? 'Vence' : 'Fecha pago'}</th>
                                    <th style={{ textAlign: 'center' }}>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>{renderBody()}</tbody>
                        </table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
                        <span className="text-muted small">
                            {!isLoading && totalRecords > 0 ? `Mostrando ${startIndex}-${endIndex} de ${totalRecords} links` : ''}
                        </span>
                        {totalPage > 1 && !isLoading ? <Paginator totalPage={totalPage} pageActual={pageActual} onChange={(actual) => setPageActual(actual)} /> : <div />}
                    </div>
                </div>
            </div>

            <div style={{ height: '60px' }}></div>

            {errorMessage.length > 0 && (
                <Message type='error' message={errorMessage} closeDialog={() => setErrorMessage('')} handleOk={() => setErrorMessage('')} backgroundColor='rgba(0,0,0,0.4)' />
            )}

            {successMessage.length > 0 && (
                <Message type='success' title='Exitoso' message={successMessage} closeDialog={() => setSuccessMessage('')} handleOk={() => setSuccessMessage('')} backgroundColor='rgba(0,0,0,0.4)' />
            )}

            {voidMessage.length > 0 && (
                <Message
                    type='error'
                    message={voidMessage}
                    closeDialog={() => { setVoidMessage(''); setCurrentLink(null); }}
                    handleCancel={() => { setVoidMessage(''); setCurrentLink(null); }}
                    showCancel={true}
                    handleOk={(reason) => processVoid(reason)}
                    backgroundColor='rgba(0,0,0,0.4)'
                    showReasonField={actualGroup == 'todos'}
                    reasonRequired={actualGroup == 'todos'}
                />
            )}
        </div>
    )
}


const styles = {
    container: {
        flex: 1,
        marginTop: 10,
    },
    nowrap: {
        whiteSpace: 'nowrap' as const,
    },
    ellipsis: {
        whiteSpace: 'nowrap' as const,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: 240,
    },
};
